/** Mirrors the backend ApprovalDtos. One file, so a contract change lands once. */

export type StepStatus =
  | 'PENDING'
  | 'ACTIVE'
  | 'APPROVED'
  | 'REJECTED'
  | 'SKIPPED'
  | 'CANCELLED'

export type InstanceStatus =
  | 'IN_PROGRESS'
  | 'APPROVED'
  | 'REJECTED'
  | 'RETURNED'
  | 'CANCELLED'

export type ActionType =
  | 'SUBMIT'
  | 'APPROVE'
  | 'RETURN'
  | 'REJECT'
  | 'CANCEL'
  | 'AUTO_SKIP'
  | 'DENIED'

export interface ApprovalStep {
  id: number
  levelNo: number
  levelName: string
  approverType: 'ROLE' | 'ROLE_BRANCH' | 'GROUP' | 'USER'
  status: StepStatus
  approvedCount: number
  requiredApprovals: number
  activatedAt: string | null
  completedAt: string | null
  dueAt: string | null
}

export interface ApprovalAction {
  id: number
  levelNo: number | null
  action: ActionType
  actedBy: number | null
  actedByName: string | null
  onBehalfOf: number | null
  onBehalfOfName: string | null
  actedAt: string
  note: string | null
}

/**
 * One run through the ladder.
 *
 * `steps` is the whole ladder including levels that did not apply, and
 * `totalLevels` counts only the ones somebody has to pass. The UI renders from
 * this array and never from a number it knows in advance — which is the thing
 * that lets the business drop from four levels to two without a release.
 */
export interface ApprovalTrail {
  instanceId: number
  cycleNo: number
  status: InstanceStatus
  matrixCode: string
  matrixRevision: number
  totalLevels: number
  currentLevel: number
  startedAt: string
  completedAt: string | null
  steps: ApprovalStep[]
  actions: ApprovalAction[]
}

export interface InboxEntry {
  id: number
  docNo: string
  status: string
  docDate: string
  customerName: string
  projectCode: string | null
  branchId: string
  currency: string
  totalAmt: number
  levelNo: number
  levelName: string
  totalLevels: number
  waitingSince: string | null
  dueAt: string | null
  overdue: boolean
  /** True when the user is standing in for someone under a delegation. */
  asDelegate: boolean
}

export interface WorkflowStatusResponse {
  status: string
  message: string
}

export const STEP_LABEL: Record<StepStatus, string> = {
  PENDING: 'Menunggu giliran',
  ACTIVE: 'Menunggu keputusan',
  APPROVED: 'Disetujui',
  REJECTED: 'Ditolak',
  SKIPPED: 'Tidak berlaku',
  CANCELLED: 'Dibatalkan',
}

export const ACTION_LABEL: Record<ActionType, string> = {
  SUBMIT: 'Dikirim untuk persetujuan',
  APPROVE: 'Disetujui',
  RETURN: 'Dikembalikan',
  REJECT: 'Ditolak',
  CANCEL: 'Dibatalkan',
  AUTO_SKIP: 'Dilewati otomatis',
  DENIED: 'Akses ditolak',
}

export const INSTANCE_LABEL: Record<InstanceStatus, string> = {
  IN_PROGRESS: 'Sedang berjalan',
  APPROVED: 'Disetujui',
  REJECTED: 'Ditolak',
  RETURNED: 'Dikembalikan',
  CANCELLED: 'Dibatalkan',
}
